import { basePath } from "./clerk";

const JOIN_WINDOW_LEAD_MS = 15 * 60 * 1000;
const JOIN_WINDOW_TAIL_MS = 2 * 60 * 60 * 1000;
const DEFAULT_SESSION_MINUTES = 90;

export type LiveSessionLike = {
  zoomScheduledAt: string | null | undefined;
  zoomUrl: string | null | undefined;
  zoomLiveStartedAt?: string | null | undefined;
};

// "Can the student tap Join right now?" — true when the teacher has actively
// flipped the lesson to live OR we're inside the soft window around the
// scheduled time. Mirrors the server-side cutoff used to build the .ics.
export function isJoinable(lesson: LiveSessionLike, now: Date = new Date()): boolean {
  if (!lesson.zoomUrl) return false;
  if (lesson.zoomLiveStartedAt) return true;
  if (!lesson.zoomScheduledAt) return false;
  const start = new Date(lesson.zoomScheduledAt).getTime();
  const t = now.getTime();
  return t >= start - JOIN_WINDOW_LEAD_MS && t <= start + JOIN_WINDOW_TAIL_MS;
}

// `YYYYMMDDTHHMMSSZ` UTC stamp used by both Google Calendar's render URL and
// our own .ics emitter. Kept identical to the server format on purpose.
function gcalStamp(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

// Build a Google Calendar "create event" URL. Per-event only — Google has no
// way to bulk-add multiple events through a single template URL, so users who
// want the whole cohort use the .ics download instead.
export function googleCalendarUrl(opts: {
  title: string;
  start: Date;
  end?: Date;
  description?: string;
  location?: string;
}): string {
  const end =
    opts.end ?? new Date(opts.start.getTime() + DEFAULT_SESSION_MINUTES * 60_000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: opts.title,
    dates: `${gcalStamp(opts.start)}/${gcalStamp(end)}`,
  });
  if (opts.description) params.set("details", opts.description);
  if (opts.location) params.set("location", opts.location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// `.ics` download URL for an enrollment. Uses the same `basePath` helper as
// the rest of the app so the link works whether mainloop is mounted at `/`
// or under a subpath in production.
export function enrollmentIcsUrl(enrollmentId: number): string {
  return `${basePath}/api/enrollments/${enrollmentId}/calendar.ics`;
}

// Display the lesson's scheduled time in the visitor's local time zone with
// a short tz abbreviation so a student in PT and a teacher in ET both see the
// "real" wall-clock time, no mental math.
const _localFmt = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});
export function formatLocalSessionTime(iso: string): string {
  return _localFmt.format(new Date(iso));
}
